import { useDisclosure } from "@heroui/react";
import { InputField, ModalBuilder, ModalError } from "../modalBuilder";
import { api } from "@/api";
import { toast } from "react-toastify";
import { useAppSelector } from "@/hooks/useAppSelector.ts";
import useErrorHandling from "@/hooks/useErrorHandling";
import { useState } from "react";
import { loadHandbooks } from "@/utils/loadHandbooks";
import { PromptAddressModal } from "./address/promptAddressModal";
import { OfficeLocation } from "@/api/locations";

export const createLocationFields: InputField[] = [
  {
    label: "Название локации",
    name: "display_name",
    placeholder: "Коворкинг на Мясницкой",
    type: "text",
    isRequired: true,
  },
  {
    label: "Адрес",
    name: "address",
    placeholder: "ул Краснобогатырская...",
    type: "text",
    isRequired: true,
  },
  {
    label: "Город",
    name: "city_name",
    placeholder: "Выберите город",
    type: "city",
    isRequired: true,
  },
  {
    label: "Район",
    name: "district",
    placeholder: "Преображенский",
    type: "text",
    isRequired: true,
  },
  {
    label: "Кол-во этажей",
    name: "floors_count",
    placeholder: "4",
    type: "text",
    isRequired: true,
  },
  {
    label: "Общая площадь",
    name: "total_area",
    placeholder: "100",
    type: "text",
    isRequired: true,
  },
  {
    label: "Станция метро",
    name: "metro_station",
    placeholder: "Черкизовская",
    type: "text",
    isRequired: false,
  },
  {
    label: "Класс здания",
    name: "building_type",
    placeholder: "Класс здания",
    type: "building-type",
    isRequired: false,
  },
  {
    label: "Год постройки",
    name: "construction_year",
    placeholder: "2000",
    type: "text",
    isRequired: false,
  },
  {
    label: "Кадастровый номер",
    name: "cadastral_number",
    placeholder: "1234567890",
    type: "text",
    isRequired: false,
  },
];

export function CreateLocationModal({
  disclosure,
  onLocationCreated,
}: {
  disclosure: ReturnType<typeof useDisclosure>;
  onLocationCreated?: () => void;
}) {
  const { isOpen, onOpenChange } = disclosure;
  const businessProfile = useAppSelector(
    (state) => state.businessProfile.businessProfile,
  );
  const [modalErrors, setModalErros] = useState<ModalError[]>([]);
  const [data, setData] = useState<Partial<OfficeLocation>>({});
  const [addresses, setAddresses] = useState<string[]>([]);

  const promptAddressModalDisclosure = useDisclosure();

  const handleError = useErrorHandling();

  const onPromptAddress = (data: Partial<OfficeLocation>) => {
    return new Promise<boolean>((resolve) => {
      api.locations
        .create({
          display_name: data.display_name || "",
          address: data.address || "",
          city_name: data.city_name || "",
          metro_station: data.metro_station || "",
          district: data.district || "",
          business_profile: businessProfile.id,
          // @ts-ignore
          building_type: data.building_type || null,
          latitude: data.latitude ? parseFloat(data.latitude.toString()) : 0,
          longitude: data.longitude ? parseFloat(data.longitude.toString()) : 0,
          // @ts-ignore
          total_area: data.total_area || null,
          // @ts-ignore
          floors_count: data.floors_count || null,
          // @ts-ignore
          construction_year: data.construction_year || null,
          // @ts-ignore
          cadastral_number: data.cadastral_number || null,
        })
        .then(() => {
          promptAddressModalDisclosure.onClose();
          loadHandbooks({ handbooks: ["locations", "cities"] });
          disclosure.onClose();
          onLocationCreated?.();
          resolve(true);
        })
        .catch((err) => {
          const { errorMessage, parsedErrors } = handleError(err);
          setModalErros(parsedErrors || []);

          toast.error(errorMessage);
          resolve(false);
        });
    });
  };

  const onSubmit = (data: Partial<OfficeLocation>) => {
    return new Promise<boolean>((resolve) => {
      api.locations
        .getAddresses(data.address || "")
        .then((res) => {
          if (res.data.length > 0) {
            disclosure.onClose();
            promptAddressModalDisclosure.onOpen();
            setData(data);
            setAddresses(res.data);
            resolve(false);
          } else {
            onPromptAddress(data)
              .then((res) => {
                resolve(res);
              })
              .catch((err) => {
                const { errorMessage, parsedErrors } = handleError(err);
                setModalErros(parsedErrors || []);
                toast.error(errorMessage);
                resolve(false);
              });
          }
        })
        .catch((err) => {
          const { errorMessage, parsedErrors } = handleError(err);
          setModalErros(parsedErrors || []);
          toast.error(errorMessage);
          resolve(false);
        });
    });
  };

  return (
    <>
      <ModalBuilder
        title="Создание локации"
        modalErrors={modalErrors}
        fields={createLocationFields}
        isOpen={isOpen}
        isSensitiveData={false}
        onOpenChange={onOpenChange}
        onSubmit={onSubmit}
      />
      <PromptAddressModal
        disclosure={promptAddressModalDisclosure}
        data={data}
        addresses={addresses}
        onConfirm={onPromptAddress}
      />
    </>
  );
}
